import { useCallback, useEffect, useState } from 'react';
import {
  DataTable, rememberPageSize, storedPageSize, type Column,
} from '../../components/DataTable';
import { ConfirmDialog } from '../../components/Feedback';
import { Roles, useAuth, useToast } from '../../app/providers';
import { api, toQuery, type Paged } from '../../lib/api';
import { PageHeader, PaidBadge, Stat, Stats } from '../../components/PageHeader';
import { IconRefresh, IconTrash } from '../../components/Icons';
import {
  FilterBar, MONTH_OPTIONS, defaultFilters, type FilterDef, type FilterValues,
} from '../../components/FilterBar';
import { useLookups, YEAR_OPTIONS } from '../../lib/lookups';

type FeesRow = {
  id: number;
  studentId: number;
  studentName: string | null;
  feesType: string | null;
  grade: string | null;
  subject: string | null;
  groupName: string | null;
  month: number | null;
  year: number | null;
  amount: number;
  paidAmount: number;
  remainingAmount: number;
  isSubmitted: boolean;
};

/**
 * Header totals, scoped to the filters rather than the page.
 *
 * Aggregated server-side over the same filtered query the grid pages, so paging never moves
 * these numbers and applying a filter always does.
 */
type FeesSummary = {
  totalCount: number;
  billed: number;
  collected: number;
  settledCount: number;
  outstanding: number;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const money = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Three states, not two: an overpaid or part-paid fee is neither "due" nor "paid". */
function statusOf(row: FeesRow) {
  return <PaidBadge paid={row.isSubmitted} partial={row.paidAmount > 0} />;
}

export function FeesListPage() {
  const toast = useToast();
  const { hasRole } = useAuth();
  const lookups = useLookups();

  const FILTERS: FilterDef[] = [
    { key: 'id', label: 'Fee #', type: 'number' },
    { key: 'student', label: 'Student name', type: 'text', wide: true },
    { key: 'studentId', label: 'Student ID', type: 'number' },
    { key: 'feesTypeId', label: 'Type', type: 'select', options: lookups.feesTypes },
    { key: 'gradeId', label: 'Grade', type: 'select', options: lookups.grades },
    { key: 'subjectId', label: 'Subject', type: 'select', options: lookups.subjects },
    { key: 'month', label: 'Month', type: 'select', options: MONTH_OPTIONS },
    { key: 'year', label: 'Year', type: 'select', options: YEAR_OPTIONS },
    { key: 'amountFrom', label: 'Amount from', type: 'number' },
    { key: 'amountTo', label: 'Amount to', type: 'number' },
    {
      key: 'status', label: 'Status', type: 'select',
      options: [
        { value: 'paid', label: 'Paid' },
        { value: 'partial', label: 'Partial' },
        { value: 'due', label: 'Due' },
      ],
    },
  ];

  // Lazy initialiser: FILTERS is rebuilt each render, but the opening values are only the
  // ones this screen started with.
  const [filters, setFilters] = useState<FilterValues>(() => defaultFilters(FILTERS));

  const [data, setData] = useState<Paged<FeesRow> | null>(null);
  const [summary, setSummary] = useState<FeesSummary | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(storedPageSize);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<FeesRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [generating, setGenerating] = useState(false);

  const filterKey = JSON.stringify(filters);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const active: Record<string, string> = {};
      for (const [key, value] of Object.entries(JSON.parse(filterKey) as FilterValues)) {
        if (value !== '') active[`filter[${key}]`] = value;
      }

      // Issued together from the same filter set, so the header can never describe a
      // different selection from the rows. The summary ignores page/pageSize server-side.
      const [result, totals] = await Promise.all([
        api.get<Paged<FeesRow>>(`/api/studentfees${toQuery({ page, pageSize, ...active })}`),
        api.get<FeesSummary>(`/api/studentfees/summary${toQuery(active)}`),
      ]);

      setData(result);
      setSummary(totals);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load fees.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterKey]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The legacy "Generate Records" action: create this month's fee row for every enrolment
   * that has not got one, skipping any that already exist.
   *
   * The count matters. A recurring job already generates these at 12:01 on the 1st of each
   * month, so pressing this later in the month usually has nothing left to do - and saying
   * "generated" when nothing was created makes a working button look broken. Report what
   * actually happened instead.
   */
  async function generateThisMonth() {
    setGenerating(true);

    try {
      const result = await api.post<{
        created: number;
        eligible: number;
        alreadyExisted: number;
        month: number;
        year: number;
        skipped: boolean;
      }>('/api/studentfees/generate-monthly');

      // The period comes back from the server, in Pakistan time - the clock the rule runs on.
      const period = `${MONTH_NAMES[result.month - 1] ?? result.month}/${result.year}`;

      if (result.skipped) {
        // Another generation held the gate. Nothing ran, so do not report a count.
        toast.push('warning', 'A fee generation is already running. Try again in a moment.');
      } else if (result.created > 0) {
        toast.success(
          `Created ${result.created} fee ${result.created === 1 ? 'record' : 'records'} for ${period}.`,
        );
        await load();
      } else if (result.eligible === 0) {
        // Nothing was even considered: no enrolment passed IsEnrolled + active + not deleted.
        toast.push('warning', 'No enrolled students to bill — nothing was generated.');
      } else {
        toast.push(
          'info',
          `Nothing to generate — all ${result.eligible} enrolments already have a fee for ${period}.`,
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not generate the fees.');
    } finally {
      setGenerating(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;

    setDeleting(true);
    try {
      await api.delete(`/api/studentfees/monthly/${pendingDelete.id}`);
      toast.success('Fees record deleted.');
      setPendingDelete(null);
      await load();
    } catch (err) {
      // The server refuses when payments exist against the fee. Surface its wording.
      toast.error(err instanceof Error ? err.message : 'Could not delete the fees record.');
    } finally {
      setDeleting(false);
    }
  }

  const columns: Column<FeesRow>[] = [
    { key: 'id', header: '#', render: (r) => r.id, width: '70px' },
    {
      key: 'student',
      header: 'Student',
      render: (r) => (r.studentName ? `${r.studentName} (STD-${r.studentId})` : '—'),
    },
    { key: 'type', header: 'Type', render: (r) => r.feesType ?? '—' },
    {
      key: 'course',
      header: 'Course',
      // Null for a registration fee, which belongs to a student rather than a course.
      render: (r) => [r.grade, r.subject, r.groupName].filter(Boolean).join(' · ') || '—',
    },
    {
      key: 'period',
      header: 'Period',
      render: (r) => (r.month && r.year ? `${r.month}/${r.year}` : '—'),
    },
    { key: 'amount', header: 'Amount', align: 'right', render: (r) => <span className="money">{money(r.amount)}</span> },
    { key: 'paid', header: 'Paid', align: 'right', render: (r) => <span className="money">{money(r.paidAmount)}</span> },
    {
      key: 'remaining',
      header: 'Remaining',
      align: 'right',
      render: (r) => <span className="money">{money(r.remainingAmount)}</span>,
    },
    { key: 'status', header: 'Status', render: statusOf },
  ];

  // Deleting a fee is Director-only on the server; do not offer it to a Cashier.
  if (hasRole(Roles.Director)) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <span className="dt__rowactions">
          <button
            type="button"
            className="btn btn--sm btn--danger-quiet"
            onClick={() => setPendingDelete(r)}
          >
            <IconTrash />
            Delete
          </button>
        </span>
      ),
    });
  }

  // Whether any filter is narrowing the list, so the cards can say what they are counting.
  const filtered = Object.values(filters).some((value) => value !== '');

  return (
    <>
      <PageHeader
        title="All Fees"
        subtitle="Every fee line across all students."
        actions={
          <>
            {/* The legacy sidebar's "Generate Records" (GenerateAllStudentMonthlyFeeses).
                Creates this month's fee row for every enrolment that has not got one; it
                skips any that already exist, so pressing it twice is harmless. */}
            <button
              type="button"
              className="btn"
              disabled={generating}
              title="Create this month's fee rows for every enrolment that has none yet"
              onClick={() => void generateThisMonth()}
            >
              {generating ? 'Generating…' : 'Generate this month'}
            </button>

            <button
              type="button"
              className="btn btn--icon"
              onClick={() => void load()}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <IconRefresh />
            </button>
          </>
        }
      />

      {summary && summary.totalCount > 0 && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Stats>
            <Stat label="Billed" value={money(summary.billed)} tone="neutral" />
            <Stat label="Collected" value={money(summary.collected)} tone="emerald" />
            <Stat label="Outstanding" value={money(summary.outstanding)} tone="rose" />
            <Stat
              label="Settled"
              value={`${summary.settledCount} / ${summary.totalCount}`}
              tone="sky"
              hint={filtered ? 'Matching the filters applied' : 'All fee lines'}
            />
          </Stats>
        </div>
      )}

      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        error={error}
        rowKey={(r) => r.id}
        emptyMessage="No fees records found."
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          rememberPageSize(size);
          setPageSize(size);
          setPage(1);
        }}
        above={
          <FilterBar
            filters={FILTERS}
            values={filters}
            onApply={(next) => { setFilters(next); setPage(1); }}
            busy={loading}
          />
        }
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        destructive
        busy={deleting}
        title="Delete this fees record?"
        confirmLabel="Delete"
        message={
          pendingDelete ? (
            <>
              <p>
                Fee <strong>#{pendingDelete.id}</strong> for{' '}
                <strong>{pendingDelete.studentName ?? 'this student'}</strong>, amount{' '}
                <strong>{money(pendingDelete.amount)}</strong>.
              </p>
              <p>This cannot be undone.</p>
            </>
          ) : null
        }
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
